// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, EMPTY, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, finalize, map, mergeMap, withLatestFrom } from "rxjs/operators"
import * as Actions from "../actions"
import * as TagsSlice from "../slice"
import { TagId } from "../slice"
import { RootState } from "../../../app/store"
import * as Grpc from "grpc-web"
import { GetTagRequest, } from "generated/legebill/site/tag/v4/tag_pb"
import { grpc } from "app/rpc/executor"
import { tagServiceClient } from "app/rpc/client"
import { Tag } from "generated/legebill/type/tag_pb"
import { never } from "grpc-web-rx"
import { isGrpcError } from "../../../lib/rpcutils"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type GetTagRpc = (request: GetTagRequest) => Observable<Tag>

// Track in-flight requests to prevent duplicates
const inFlightRequests = new Set<TagId>()

// Export for testing
export const __test__ = { inFlightRequests }

export const getTag = (getTagRpc: GetTagRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.getTag.match),
      withLatestFrom(state$),
      mergeMap(([{ payload: { request, swallowFailure }}, state]) => {
        const tagId = request.getTagId()
        // Skip if tag already in cache or request is already in flight
        if (state.tags.tagsCache?.[tagId] || inFlightRequests.has(tagId)) {
          return EMPTY
        }

        // Mark as in flight
        inFlightRequests.add(tagId)

        return of(request)
          .pipe(
            mergeMap(getTagRpc),
            map(t => t.toObject()),
            map(TagsSlice.setTag),
            catchError(e => handleError(e, tagId, swallowFailure)),
            // Clean up in-flight tracking on completion or error
            finalize(() => inFlightRequests.delete(tagId))
          )
      })
    )

const getTagRpc = (request: GetTagRequest): Observable<Tag> =>
  grpc(() => tagServiceClient.getTag(request, null), never)

const handleError = (e: unknown, tagId: TagId, swallowFailure: boolean | undefined) => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === Grpc.StatusCode.NOT_FOUND) {
      const actions: Action[] = [
        showSnackBar({
          message: t({ id: "msg_get_tag_not_found", message: "Tag not found. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
          messageType: "ERROR"
        }),
        TagsSlice.removeFromCache(tagId),
      ]

      if (swallowFailure) {
        actions.shift()
      }

      return concat(actions)
    }

    if (code === Grpc.StatusCode.PERMISSION_DENIED) {
      const actions: Action[] = [
        showSnackBar({
          message: t({ id: "msg_get_tag_permission_denied", message: "Failed to load tag, permission denied. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
          messageType: "ERROR"
        }),
      ]

      if (swallowFailure) {
        actions.shift()
      }

      return concat(actions)
    }
  }

  const actions = [
    showSnackBar({
      message: t({ id: "msg_get_tag_failed", message: "Failed to load tag. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
      messageType: "ERROR",
    }),
  ]

  if (swallowFailure) {
    actions.shift()
  }

  return concat(actions)
}

export default getTag(getTagRpc)
